/**
 * Engagement evidence hooks (W5.8) — a company's decision records.
 *
 * Query keys nest under `['engagement-evidence', 'talent', talentId]` so a
 * mutation that takes a capture (a verdict, a stage move, an on-demand capture)
 * invalidates a talent's list and verdict history in one call, and under
 * `['engagement-evidence', 'capture', captureId]` for one record and its diff.
 *
 * The retained-file URL is deliberately NOT a query: every fetch is an audited
 * read server-side, so it must happen when the viewer asks to open the file —
 * never on render, on focus, or on a background refetch.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSdkContext } from '../providers/SdkProvider';
import {
  createEngagementEvidenceService,
  type EvidenceRedactionReason,
} from '../services/engagement-evidence.service';

export const engagementEvidenceKeys = {
  all: ['engagement-evidence'] as const,
  talent: (talentId: string) => [...engagementEvidenceKeys.all, 'talent', talentId] as const,
  talentCaptures: (talentId: string, journeyId?: string) =>
    [...engagementEvidenceKeys.talent(talentId), 'captures', journeyId ?? ''] as const,
  verdictHistory: (talentId: string, journeyId: string) =>
    [...engagementEvidenceKeys.talent(talentId), 'verdict-history', journeyId] as const,
  capture: (captureId: string) => [...engagementEvidenceKeys.all, 'capture', captureId] as const,
  diff: (captureId: string) => [...engagementEvidenceKeys.capture(captureId), 'diff'] as const,
};

/** Every capture this company holds on the talent, newest first. */
export function useTalentEvidenceCaptures(
  talentId: string,
  options?: { journeyId?: string; enabled?: boolean }
) {
  const { client } = useSdkContext();
  const service = createEngagementEvidenceService(client);
  return useQuery({
    queryKey: engagementEvidenceKeys.talentCaptures(talentId, options?.journeyId),
    queryFn: () => service.listForTalent(talentId, { journeyId: options?.journeyId }),
    enabled: (options?.enabled ?? true) && !!talentId,
  });
}

/** One capture with every item (withheld markers included). */
export function useEvidenceCapture(captureId: string, options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const service = createEngagementEvidenceService(client);
  return useQuery({
    queryKey: engagementEvidenceKeys.capture(captureId),
    queryFn: () => service.getCapture(captureId),
    enabled: (options?.enabled ?? true) && !!captureId,
  });
}

/** At decision · current, per item. */
export function useEvidenceDiff(captureId: string, options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const service = createEngagementEvidenceService(client);
  return useQuery({
    queryKey: engagementEvidenceKeys.diff(captureId),
    queryFn: () => service.getDiff(captureId),
    enabled: (options?.enabled ?? true) && !!captureId,
  });
}

/** Every TestIt verdict the company recorded on the talent; the current one flagged. */
export function useVerdictHistory(
  talentId: string,
  journeyId: string,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const service = createEngagementEvidenceService(client);
  return useQuery({
    queryKey: engagementEvidenceKeys.verdictHistory(talentId, journeyId),
    queryFn: () => service.getVerdictHistory(talentId, journeyId),
    enabled: (options?.enabled ?? true) && !!talentId,
  });
}

/** Take an explicit capture on a live relationship. */
export function useCaptureEvidence(talentId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createEngagementEvidenceService(client);
  return useMutation({
    mutationFn: (options?: { journeyId?: string }) => service.captureOnDemand(talentId, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: engagementEvidenceKeys.talent(talentId) });
    },
  });
}

/**
 * Request a short-lived URL for a retained file. A mutation, not a query: each
 * call is an audited read on the server, made when the viewer opens the file.
 */
export function useRetainedBlobUrl() {
  const { client } = useSdkContext();
  const service = createEngagementEvidenceService(client);
  return useMutation({
    mutationFn: (args: { captureId: string; blobRefId: string }) =>
      service.getRetainedBlobUrl(args.captureId, args.blobRefId),
  });
}

/** The controller's redaction of one item. */
export function useRedactEvidenceItem(captureId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createEngagementEvidenceService(client);
  return useMutation({
    mutationFn: (args: { itemId: string; reason: EvidenceRedactionReason }) =>
      service.redactItem(captureId, args.itemId, args.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: engagementEvidenceKeys.capture(captureId) });
    },
  });
}
